import React, { useEffect, useState, useCallback } from "react";
import { api, formatDuration, formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Trophy, Crown, Medal, RotateCcw, AlertTriangle, Pencil, Trash2, X } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

const SCOPES = [
  { key: "current", label: "Ciclo Corrente" },
  { key: "lifetime", label: "Sempre" },
];

function formatDateTime(iso) {
  if (!iso) return "-";
  try {
    return new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}

export default function Classifica() {
  const { user } = useAuth();
  const isDirettore = user?.role === "Proprietario";
  const [scope, setScope] = useState("current");
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  // Modifica turni utente
  const [shiftsUser, setShiftsUser] = useState(null);
  const [userShifts, setUserShifts] = useState({ shifts: [], total_seconds: 0 });
  const [userShiftsLoading, setUserShiftsLoading] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/shifts/leaderboard?scope=${scope}`)
      .then((r) => setEntries(r.data))
      .finally(() => setLoading(false));
  }, [scope]);

  useEffect(() => { load(); }, [load]);

  const resetHours = async (targetScope) => {
    const msg = targetScope === "current"
      ? "Resettare le ore del CICLO CORRENTE per tutti i dipendenti? (I turni già pagati resteranno)"
      : "ATTENZIONE: eliminare TUTTE le ore di sempre (anche già pagate)? Operazione irreversibile.";
    if (!window.confirm(msg)) return;
    try {
      const { data } = await api.post("/shifts/reset", { scope: targetScope });
      toast.success(`Ore resettate · ${data.deleted} turni eliminati`);
      setResetOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const loadUserShifts = useCallback(async (userId) => {
    setUserShiftsLoading(true);
    try {
      const { data } = await api.get(`/shifts/by-user/${userId}?scope=${scope}`);
      setUserShifts(data);
    } catch (e) {
      console.warn("Errore carico turni utente:", e);
      setUserShifts({ shifts: [], total_seconds: 0 });
    } finally { setUserShiftsLoading(false); }
  }, [scope]);

  const openUserShifts = (u) => {
    setShiftsUser(u);
    loadUserShifts(u.user_id);
  };

  const closeUserShifts = () => {
    setShiftsUser(null);
    setUserShifts({ shifts: [], total_seconds: 0 });
  };

  const updateShiftDuration = async (shiftId, newHours, newMinutes) => {
    const h = Math.max(0, parseInt(newHours || 0, 10) || 0);
    const m = Math.max(0, Math.min(59, parseInt(newMinutes || 0, 10) || 0));
    const duration = h * 3600 + m * 60;
    try {
      await api.patch(`/shifts/${shiftId}`, { duration_seconds: duration });
      toast.success("Durata turno aggiornata");
      if (shiftsUser) loadUserShifts(shiftsUser.user_id);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const deleteShift = async (shiftId) => {
    if (!window.confirm("Eliminare questo turno? L'operazione è irreversibile.")) return;
    try {
      await api.delete(`/shifts/${shiftId}`);
      toast.success("Turno eliminato");
      if (shiftsUser) loadUserShifts(shiftsUser.user_id);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const max = entries[0]?.total_seconds || 1;

  return (
    <div className="w-full">
      <PageHeader
        icon={Trophy}
        title="Ore Settimanali"
        subtitle={isDirettore ? "Ranking operatori · Clicca su un nome per modificare i turni." : "Ranking operatori per ore di servizio."}
        right={
          <div className="flex flex-wrap items-center gap-1 ot-panel p-1">
            {SCOPES.map((s) => (
              <button
                key={s.key}
                data-testid={`period-${s.key}`}
                onClick={() => setScope(s.key)}
                className={`px-3 py-1.5 text-xs ot-heading uppercase tracking-widest rounded transition-colors ${scope === s.key ? "bg-blue-400 text-black" : "text-slate-400 hover:text-blue-400"}`}
              >
                {s.label}
              </button>
            ))}
          </div>
        }
      />

      {/* Podium */}
      {entries.length >= 3 && (
        <div className="grid grid-cols-3 gap-2 sm:gap-4 mb-8 ot-fade-up">
          {[entries[1], entries[0], entries[2]].map((e, idx) => {
            const rank = idx === 0 ? 2 : idx === 1 ? 1 : 3;
            const heights = { 1: "h-40", 2: "h-32", 3: "h-28" };
            const colors = { 1: "text-blue-400 border-blue-400/50", 2: "text-slate-300 border-slate-300/50", 3: "text-amber-500 border-amber-500/50" };
            return (
              <div key={e.user_id} className="flex flex-col items-center justify-end min-w-0">
                <div className={`ot-heading font-black text-sm sm:text-lg uppercase text-center w-full truncate px-1 ${colors[rank].split(" ")[0]}`} title={e.display_name || e.username}>
                  {e.display_name || e.username}
                </div>
                <div className="ot-mono text-[0.65rem] sm:text-xs text-slate-500 mb-2">{formatDuration(e.total_seconds)}</div>
                <div className={`w-full ot-panel-elev ${heights[rank]} flex items-center justify-center border-t-2 ${colors[rank].split(" ")[1]}`}>
                  <div className="flex flex-col items-center">
                    {rank === 1 && <Crown className="w-6 h-6 text-blue-400 mb-1" />}
                    {rank === 2 && <Medal className="w-5 h-5 text-slate-300 mb-1" />}
                    {rank === 3 && <Medal className="w-5 h-5 text-amber-500 mb-1" />}
                    <div className={`ot-mono font-black text-3xl sm:text-4xl ${colors[rank].split(" ")[0]}`}>#{rank}</div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Full ranking */}
      <div className="ot-panel-elev p-6 ot-fade-up" style={{ animationDelay: "80ms" }}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="ot-heading text-2xl font-bold text-slate-100">
            {scope === "current" ? "Ranking Ciclo Corrente" : "Ranking di Sempre"}
          </h2>
          <div className="flex items-center gap-3">
            {isDirettore && (
              <button
                data-testid="reset-hours-btn"
                onClick={() => setResetOpen(!resetOpen)}
                className="ot-btn-ghost flex items-center gap-1.5 !text-red-400 !border-red-500/40 hover:!bg-red-500/10"
              >
                <RotateCcw className="w-3.5 h-3.5" />
                Resetta Ore
              </button>
            )}
            <span className="ot-mono text-[0.7rem] text-slate-500 tracking-widest">{entries.length} OPERATORI</span>
          </div>
        </div>

        {isDirettore && resetOpen && (
          <div className="mb-4 border border-red-500/30 rounded p-4 bg-red-500/[0.03] ot-fade-up">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="w-4 h-4 text-red-400" />
              <div className="ot-heading text-sm font-bold text-red-400 uppercase tracking-widest">Reset Ore Ciclo Corrente</div>
            </div>
            <p className="text-slate-400 text-xs mb-3">
              Azzera le ore del ciclo corrente per tutti i dipendenti. I turni già pagati (storico) restano intatti.
            </p>
            <div className="flex flex-wrap gap-2">
              <button
                data-testid="reset-hours-current"
                onClick={() => resetHours("current")}
                className="ot-btn-ghost !text-amber-400 !border-amber-500/40 hover:!bg-amber-500/10"
              >
                Conferma Reset Ciclo Corrente
              </button>
              <button onClick={() => setResetOpen(false)} className="ot-btn-ghost">Annulla</button>
            </div>
          </div>
        )}

        {loading ? (
          <div className="py-12 text-center text-slate-500 ot-mono">Caricamento...</div>
        ) : entries.length === 0 ? (
          <div className="py-16 text-center">
            <Trophy className="w-10 h-10 mx-auto text-slate-600 mb-3" />
            <div className="text-slate-400">Nessun dato disponibile.</div>
          </div>
        ) : (
          <div className="space-y-2">
            {entries.map((e, i) => {
              const pct = Math.max(2, Math.round((e.total_seconds / max) * 100));
              const initials = (e.display_name || e.username).split(/[\s._-]+/).map((s) => s[0]).join("").slice(0, 2).toUpperCase();
              const clickable = isDirettore;
              return (
                <div
                  key={e.user_id}
                  onClick={clickable ? () => openUserShifts(e) : undefined}
                  className={`ot-panel p-3 flex items-center gap-4 transition-colors ${clickable ? "cursor-pointer hover:border-blue-400/50 hover:bg-blue-400/[0.04]" : "hover:border-blue-400/30"}`}
                  data-testid={`leaderboard-row-${i}`}
                  title={clickable ? "Clicca per modificare i turni" : ""}
                >
                  <div className="ot-mono font-black text-2xl text-slate-500 w-10 text-center">#{i + 1}</div>
                  <div className="w-10 h-10 rounded-md border border-blue-400/40 bg-blue-400/10 flex items-center justify-center ot-heading font-bold text-blue-400 text-sm">
                    {initials}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-3 mb-1">
                      <div className="min-w-0">
                        <div className={`ot-heading font-bold uppercase text-sm truncate ${clickable ? "text-blue-300" : "text-slate-100"}`} title={e.display_name || e.username}>{e.display_name || e.username}</div>
                        <div className="ot-mono text-[0.65rem] text-blue-400/70 tracking-widest uppercase truncate">{e.role}</div>
                      </div>
                      <div className="text-right shrink-0">
                        <div className="ot-mono font-bold text-blue-400">{formatDuration(e.total_seconds)}</div>
                        <div className="ot-mono text-[0.65rem] text-slate-500 tracking-widest">{e.shifts_count} TURNI</div>
                      </div>
                    </div>
                    <div className="h-1 bg-white/5 rounded overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-blue-400 to-blue-500" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {shiftsUser && (
        <UserShiftsModal
          user={shiftsUser}
          shifts={userShifts}
          loading={userShiftsLoading}
          scope={scope}
          onSave={updateShiftDuration}
          onDelete={deleteShift}
          onClose={closeUserShifts}
        />
      )}
    </div>
  );
}

function UserShiftsModal({ user, shifts, loading, scope, onSave, onDelete, onClose }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="user-shifts-modal">
      <div className="ot-panel-elev max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 z-10 bg-[#0a1024] border-b border-blue-400/20 p-5 flex items-center justify-between">
          <div>
            <div className="ot-label">// TURNI DIPENDENTE · {scope === "current" ? "CICLO CORRENTE" : "STORICO"}</div>
            <h2 className="ot-heading text-2xl font-black uppercase tracking-tight text-slate-100">{user.display_name || user.username}</h2>
            <div className="ot-mono text-[0.65rem] text-blue-400/80 tracking-widest mt-0.5">
              @{user.username} · {user.role}
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/5 rounded transition-colors" data-testid="close-user-shifts">
            <X className="w-5 h-5 text-slate-400" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div className="ot-panel p-4">
            <div className="ot-label mb-1">TOTALE ORE</div>
            <div className="ot-mono text-2xl font-black text-blue-400">{formatDuration(shifts.total_seconds || 0)}</div>
          </div>

          {loading ? (
            <div className="py-12 text-center text-slate-500 ot-mono">Caricamento...</div>
          ) : (shifts.shifts || []).length === 0 ? (
            <div className="py-12 text-center text-slate-500">Nessun turno registrato in questo periodo.</div>
          ) : (
            <div className="space-y-2">
              {(shifts.shifts || []).map((s) => (
                <ShiftEditRow key={s.id} shift={s} onSave={onSave} onDelete={onDelete} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ShiftEditRow({ shift, onSave, onDelete }) {
  const initH = Math.floor((shift.duration_seconds || 0) / 3600);
  const initM = Math.floor(((shift.duration_seconds || 0) % 3600) / 60);
  const [h, setH] = useState(initH);
  const [m, setM] = useState(initM);
  const isPaid = !!shift.paid_at;
  const changed = h !== initH || m !== initM;

  return (
    <div className="ot-panel p-3 flex flex-wrap items-center gap-3" data-testid={`shift-row-${shift.id}`}>
      <div className="flex-1 min-w-[180px]">
        <div className="ot-mono text-xs text-slate-300">{formatDateTime(shift.start_at)}</div>
        <div className="ot-mono text-[0.65rem] text-slate-500 tracking-widest">
          → {shift.end_at ? formatDateTime(shift.end_at) : "in corso"}
          {shift.force_closed && " · CHIUSO FORZATO"}
          {shift.auto_closed && " · AUTO-CHIUSO"}
          {isPaid && " · PAGATO"}
        </div>
      </div>
      <div className="flex items-center gap-1">
        <input
          data-testid={`shift-hours-${shift.id}`}
          type="number"
          min="0"
          className="ot-input ot-mono w-16 text-center"
          value={h}
          onChange={(e) => setH(e.target.value)}
        />
        <span className="text-slate-500 text-xs">h</span>
        <input
          data-testid={`shift-minutes-${shift.id}`}
          type="number"
          min="0"
          max="59"
          className="ot-input ot-mono w-16 text-center"
          value={m}
          onChange={(e) => setM(e.target.value)}
        />
        <span className="text-slate-500 text-xs">m</span>
      </div>
      <button
        onClick={() => onSave(shift.id, h, m)}
        disabled={!changed}
        data-testid={`shift-save-${shift.id}`}
        className="ot-btn-ghost !py-1.5 !px-3 flex items-center gap-1 text-blue-400 disabled:opacity-40 disabled:cursor-not-allowed"
      >
        <Pencil className="w-3 h-3" /> Salva
      </button>
      <button
        onClick={() => onDelete(shift.id)}
        data-testid={`shift-delete-${shift.id}`}
        className="ot-btn-ghost !py-1.5 !px-3 flex items-center gap-1 !text-red-400 !border-red-500/40 hover:!bg-red-500/10"
      >
        <Trash2 className="w-3 h-3" /> Elimina
      </button>
    </div>
  );
}
